import { useBasket } from '~/components/routes/v2/useBasket';
import { OrderVariant } from '~/models/OrderVariant';
import { ProductType } from '~/models/product/ProductType';
import { BYOD_COMMERCIAL_PRODUCT_ID } from '~/store/campaign/byod.constants';
import { captureUXEventOnce } from './productAnalytics';

const send = (event: Event) => {
  const { event: eventName, ...eventArgs } = event;
  console.debug('GA: before_send', eventName, eventArgs);
  window.dataLayer.push(event);
};

type OmitEvent<T> = Omit<T, 'event'>;
type BasketProducts = CampaignLoadedEvent['products'];

const trackStepChangeEvent = ({
  checkout_step,
  verification_method,
  funnelVersion,
}: OmitEvent<StepChangeEvent> & { funnelVersion?: number }) => {
  const v2Step = funnelVersion === 2 ? getFunnelV2Step(checkout_step) : undefined;
  const event: StepChangeEvent = {
    event: EventTypes.Checkout,
    checkout_step: v2Step ?? checkout_step,
  };
  // add to params only if provided
  if (verification_method) {
    event.verification_method = verification_method;
  }
  send(event);

  // Posthog
  try {
    // add basket products to funnel step tracking
    let products: string[] = [];
    products = useBasket.getState().selectedProducts.map((p) => p.displayData.name);
    captureUXEventOnce(
      getPostHogEventName(v2Step, checkout_step),
      Object.assign({ products }, verification_method ? { verification_method } : undefined, funnelVersion ? { funnelVersion } : undefined),
    );
  } catch (e) {
    console.error('Error tracking step change event:', e);
  }
};
const getPostHogEventName = (v2Step: number | undefined, step: number) => {
  if (v2Step) return `bestill_funnel_step_${v2Step}_${TrackingPointsV2[v2Step]}`;
  return `bestill_funnel_step_${step}_${TrackingPoints[step]}`;
};
export const getFunnelV2Step = (step: number) => {
  const num = TrackingPoints[step];
  if (step === TrackingPoints.CustomerFilter) {
    return TrackingPointsV2.ProductComparison;
  }
  return TrackingPointsV2[num as keyof typeof TrackingPointsV2];
};

type StepChangeEventArgs =
  | {
      trackingPoint: TrackingPoints.ContactInformation;
      verificationProvider: 'bankid' | 'vipps';
      products: BasketProducts;
      funnelVersion?: number;
    }
  | {
      trackingPoint: TrackingPoints.Equipment;
      products: BasketProducts;
      funnelVersion?: number;
    }
  | {
      trackingPoint: Exclude<TrackingPoints, TrackingPoints.ContactInformation | TrackingPoints.Equipment>;
      funnelVersion?: number;
    };
export const trackChangeTrackingPointEvent = (params: StepChangeEventArgs) => {
  if (params.trackingPoint === TrackingPoints.ContactInformation) {
    // Special case: track verification provider after callback
    trackStepChangeEvent({
      checkout_step: params.trackingPoint,
      verification_method: params.verificationProvider,
      funnelVersion: params.funnelVersion,
    });
  } else {
    trackStepChangeEvent({ checkout_step: params.trackingPoint, funnelVersion: params.funnelVersion });
  }
  // On navigation to 2nd and 3rd step, track products
  if (params.trackingPoint === TrackingPoints.Equipment || params.trackingPoint === TrackingPoints.ContactInformation) {
    send({
      event: EventTypes.AddToCart,
      ecommerce: {
        value: params.products.reduce((acc, product) => acc + product.price * product.quantity, 0),
        add: {
          products: params.products.map(({ id, name, category, variant, price, quantity }) => ({
            item_id: id.toString(),
            item_name: name,
            item_category: category,
            item_variant: variant,
            price,
            quantity,
          })),
        },
      },
    });
  }
};

export const trackToggleOpenBasketEvent = (isOpen: boolean) => {
  send({
    event: isOpen ? EventTypes.ExpandableBasketOpen : EventTypes.ExpandableBasketClose,
    ecommerce: undefined,
  } satisfies ExpandableBasketEvent);
};

export const trackPurchaseEvent = ({
  products,
  partnerName,
  verification_method,
  delayedActivation,
  ...rest
}: Omit<PurchaseEvent['ecommerce'], 'items'> & {
  products: BasketProducts;
  partnerName: string | undefined;
  verification_method: string;
  funnelVersion?: number;
  delayedActivation: boolean;
}) => {
  try {
    send({
      event: EventTypes.Purchase,
      ecommerce: {
        ...rest,
        items: products.map((product) => ({
          item_id: product.id.toString(),
          item_name: product.name,
          price: product.price,
          affiliation: partnerName,
        })),
      },
    });
    // Posthog
    const allProducts = products.map((p) => p.name);
    const installation = products.filter((p) => p.category === 'installation').map((p) => p.name);
    const premium = products.filter((p) => p.category === 'premium').map((p) => p.name);
    const receivers = products.filter((p) => p.category === 'receiver').map((p) => p.name);
    const equipment = products.filter((p) => p.category === 'equipment').map((p) => p.name);
    const selectedByod = products.some((p) => p.id === BYOD_COMMERCIAL_PRODUCT_ID);

    captureUXEventOnce(`bestill_funnel_step_checkout_posted`, {
      allProducts,
      installation,
      premium,
      receivers,
      equipment,
      selectedByod,
      verification_method,
      costFirstYear: rest.value,
      funnelVersion: rest.funnelVersion,
      delayedActivation,
      ispPartner: partnerName,
    });
  } catch (e) {
    console.error('Error tracking purchase event:', e);
  }
};

export enum TrackingPoints {
  CustomerFilter = 1,
  ProductComparison = 2,
  Content = 3,
  Equipment = 4,
  // only used in new flow
  Internet = 4.5,
  Verification = 5,
  ContactInformation = 6,
  Summary = 7,
}
export enum TrackingPointsV2 {
  ProductComparison = 1,
  Content = 2,
  Equipment = 3,
  // only used in new flow
  Internet = 4,
  Verification = 5,
  ContactInformation = 6,
  Summary = 7,
}

enum EventTypes {
  Checkout = 'checkout',
  CampaignLoaded = 'campaignLoaded',
  AddToCart = 'add_to_cart',
  ExpandableBasketOpen = 'expandable-basket-open',
  ExpandableBasketClose = 'expandable-basket-close',
  Purchase = 'purchase',
  CustomerFilter = 'customer_filter',
}
type VerificationMethod = 'bankid' | 'vipps';
interface StepChangeEvent {
  event: EventTypes.Checkout;
  checkout_step: number;
  verification_method?: VerificationMethod;
}

interface CampaignLoadedEvent {
  event: EventTypes.CampaignLoaded;
  campaignCode: string;
  campaignVariant: string;
  products: Array<{ id: number; name: string; price: number; category: ProductType; quantity: number; variant?: OrderVariant }>;
  partner: string;
}

interface AddProductEvent {
  event: EventTypes.AddToCart;
  ecommerce: {
    value: number;
    add: {
      products: Array<{
        item_id: string;
        item_name: string;
        item_category: string;
        item_variant?: string;
        price: number;
        quantity: number;
      }>;
    };
  };
}

interface CustomerFilterEvent {
  event: EventTypes.CustomerFilter;
  step: number;
  distribution_model?: 'DTT' | 'IP';
  distribution_partner?: string;
  dwelling_unit?: 'SDU' | 'MDU';
}

interface ExpandableBasketEvent {
  event: EventTypes.ExpandableBasketOpen | EventTypes.ExpandableBasketClose;
  ecommerce: undefined;
}

/**
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#make_a_purchase_or_issue_a_refund
 */
interface PurchaseEvent {
  event: EventTypes.Purchase;
  ecommerce: {
    currency: 'NOK';
    /** The unique identifier of a transaction. Avoids duplicate events */
    transaction_id: string;
    /** Monetary value of event (cost first year) */
    value: number;
    /** The coupon name/code associated with the event. */
    coupon?: string;
    /** Shipping cost associated with a transaction. */
    shipping?: number;
    /** Tax cost associated with a transaction. */
    tax?: number;
    /** Purchased items */
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      /** A product affiliation to designate a supplying company or brick and mortar store location. */
      affiliation?: string;
    }>;
  };
}
type Event = StepChangeEvent | CampaignLoadedEvent | AddProductEvent | ExpandableBasketEvent | PurchaseEvent | CustomerFilterEvent;
